import React, { useContext, useMemo, useRef } from 'react';
import { View, RefreshControl, Platform, FlatList } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { PaginationNav } from '../pagination';

import { ListContext } from '../../../common/containers';
import { Spinner } from '../spinner/Spinner';
import ListFilter from './ListFilter';
import ListSettings from './ListSettings';
import { useDidUpdateEffect, usePrevious } from '../../../common/utils/hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import EmptyState from '../emptyState/EmptyState';

// Empty lists show a title and a message line, like every other empty state.
export const EmptyListComponent = ({
	emptyListMessage = 'Nothing here yet',
	emptyListHint = 'New items will appear here.',
}) => (
	<EmptyState message={emptyListMessage} hint={emptyListHint} />
);

export const List = ({
	items: itemsFromProps,
	listItemComponent: ListItem,
	onRefresh,
	isRefreshing,
	customProps,
	emptyListMessage,
	emptyListHint,
	showControls,
	assembler, // used for sorting
	autoScrollTop,
	removeTopMargin,
	customControls,
	onScroll,
	scrollEventThrottle,
	header,
}) => {
	const styles = useThemedStyles(createStyles);
	const {
		variables: {
			page,
			perPage,
			filter,
		},
		changePage,
		count,
		loadingInitial,
		items: itemsFromContext,
		changeQueryFilterDebounced,
		listId,
	} = useContext(ListContext);

	const flatListRef = useRef();
	const previousFilter = usePrevious(filter);

	// scroll to top when page change (but NOT when filter changes)
	useDidUpdateEffect(() => {
		let timeout;
		// Only auto-scroll if the filter hasn't changed
		// This prevents scrolling when user searches/filters, only when paginating
		const filterChanged = previousFilter !== filter;

		if (autoScrollTop && flatListRef?.current && !filterChanged) {
			timeout = setTimeout(() => {
				try {
					// Both FlatList and FlashList use scrollToOffset
					if (flatListRef.current?.scrollToOffset) {
						flatListRef.current.scrollToOffset({ animated: true, offset: 0 });
					} else if (flatListRef.current?.scrollToIndex) {
						// Fallback to scrollToIndex if available
						flatListRef.current.scrollToIndex({ animated: true, index: 0 });
					}
				} catch (error) {
					// Silently handle any scrolling errors to prevent crashes
					console.warn('Error scrolling to top:', error);
				}
			}, 100);
		}

		return () => clearTimeout(timeout);
	}, [page, flatListRef, autoScrollTop, filter, previousFilter]);


	// Items are from context by default (and always present); but
	// overwritten by props if passed in
	const items = itemsFromProps || itemsFromContext;

	// CURRENTLY NEVER USING LOADING, BECAUSE OF THIS ISSUE:
	// https://github.com/apollographql/react-apollo/issues/3425

	const dataWithKeys = useMemo(() => items.map(item => ({
		...item,
		key: item.id,
	})), [items]);

	const EmptyListComponentWithMessage = <EmptyListComponent emptyListMessage={emptyListMessage} emptyListHint={emptyListHint} />;

	// Only show spinner on true initial load (no data yet)
	// If we have data, keep showing it while refetching to preserve component state (especially ListFilter)
	if (loadingInitial && !items) {
		return <Spinner />;
	}

	// Web-specific container style to enable scrolling
	const webContainerStyle = Platform.OS === 'web' ? {
		flex: 1,
		minHeight: 0,
	} : {};

	const webListContainerStyle = Platform.OS === 'web' ? {
		flex: 1,
		minHeight: 0,
	} : {};

	const controls = showControls && (
		<View style={styles.controlsOuterContainer}>
			<View style={styles.controlsContainer}>
				<View style={styles.controlsLeft}>
					<ListFilter
						changeQueryFilterDebounced={changeQueryFilterDebounced}
					/>
					{
						customControls
					}
				</View>
				{
					Boolean(assembler && listId) && (
						<View style={styles.controlsRight}>
							<View
								style={{
									flexDirection: 'column',
								}}
							>
								<ListSettings assembler={assembler} />
							</View>
						</View>
					)
				}
			</View>
		</View>
	);
	// A header (e.g. a form above the list) scrolls with the items, so the
	// list keeps its height on short screens; the controls then sit under it
	// and the content gets no extra top padding (the header keeps its place).
	const listHeader = header ? <>{header}{controls}</> : undefined;

	return (
		<View style={[styles.container, { paddingTop: removeTopMargin ? 0 : 16 }, webContainerStyle]}>
			{!header && controls}
			<View style={[{ flex: 1 }, webListContainerStyle]}>
				{Platform.OS === 'web' ? (
					<FlatList
						ref={flatListRef}
						contentContainerStyle={[
							showControls && !header ? { paddingTop: 8 } : undefined,
							{ paddingBottom: 20 },
							dataWithKeys.length === 0 ? { flex: 1 } : undefined,
						]}
						style={{ flex: 1 }}
						refreshControl={!onRefresh ? undefined : (
							<RefreshControl
								refreshing={isRefreshing}
								onRefresh={onRefresh}
							/>
						)}
						data={dataWithKeys}
						extraData={customProps}
						onScroll={onScroll}
						scrollEventThrottle={scrollEventThrottle}
						renderItem={({ item, index }) => <ListItem item={item} index={index} customProps={customProps} />}
						ListFooterComponent={(
							<PaginationNav
								count={count}
								page={page}
								perPage={perPage}
								onPageChange={changePage}
							/>
						)}
						ListHeaderComponent={listHeader}
						ListEmptyComponent={EmptyListComponentWithMessage}
						keyExtractor={(item) => item.key}
					/>
				) : (
					<FlashList
						estimatedItemSize={100}
						contentContainerStyle={showControls && !header ? { paddingTop: 8, paddingBottom: 20 } : { paddingBottom: 20 }}
						refreshControl={!onRefresh ? undefined : (
							<RefreshControl
								refreshing={isRefreshing}
								onRefresh={onRefresh}
							/>
						)}
						data={dataWithKeys}
						extraData={customProps}
						onScroll={onScroll}
						scrollEventThrottle={scrollEventThrottle}
						renderItem={({ item, index, extraData }) => <ListItem item={item} index={index} customProps={extraData} />}
						ListFooterComponent={(
							<PaginationNav
								count={count}
								page={page}
								perPage={perPage}
								onPageChange={changePage}
							/>
						)}
						ListHeaderComponent={listHeader}
						ListEmptyComponent={EmptyListComponentWithMessage}
						ref={flatListRef}
					/>
				)}
			</View>
		</View>
	);
};

// The controls sit on the page canvas (not a header-coloured band) on the 18 pt gutter.
const createStyles = ({ ui: { space } }) => ({
	// No bottom margin: the list runs to the footer (dock / tab bar) and its
	// content keeps 20 pt of bottom padding, so no empty band shows above it.
	container: {
		flex: 1,
	},
	controlsOuterContainer: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	controlsContainer: {
		paddingBottom: space.xs,
		paddingHorizontal: space.gutter,
		width: '100%',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	// The filter shrinks so custom controls and Settings stay on screen on phones.
	controlsLeft: {
		flex: 1,
		minWidth: 0,
		flexDirection: 'row',
		alignItems: 'center',
	},
	controlsRight: {
		marginLeft: space.sm,
	},
});
